'use client';
import { AnimatePresence } from 'framer-motion';
import type { CSSProperties, ReactElement, RefObject } from 'react';
import { pxToRem } from '@/theme/spacing';
import { XYCHART_DEFAULT_CANVAS_HEIGHT } from './xychart-constants';
import { formatXYChartHoverValue, type XYChartPopoverModel, } from './xychart-component-model';
import type { XYChartHoverEntry } from './XYChartHoverPopover';
import { XYChartCanvas } from './XYChartCanvas';
import { XYChartHoverPopover } from './XYChartHoverPopover';
import { XYChartLegend } from './XYChartLegend';
import type { XYChartLayout, XYChartParsed } from './xychart-types';
const CANVAS_PADDING_PX = 12;
const FRAME_STYLE: CSSProperties = {
    position: 'relative',
    flex: 1,
    minHeight: XYCHART_DEFAULT_CANVAS_HEIGHT,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
};
const SCROLL_VIEWPORT_STYLE: CSSProperties = {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
};
const SCROLL_TRACK_STYLE: CSSProperties = {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    width: 'fit-content',
    minWidth: '100%',
    minHeight: '100%',
};
const CANVAS_WRAP_STYLE: CSSProperties = {
    position: 'relative',
    padding: pxToRem(CANVAS_PADDING_PX),
    display: 'inline-block',
};
const POPOVER_LAYER_STYLE: CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 5,
};
export interface XYChartFrameProps {
    parsed: XYChartParsed;
    layout: XYChartLayout | null;
    uniqueSvgId: string;
    viewportRef: RefObject<HTMLDivElement | null>;
    svgRef: RefObject<SVGSVGElement | null>;
    hoveredSeriesIndex: number | null;
    hoveredCategoryIndex: number | null;
    hoverEntries: XYChartHoverEntry[];
    popoverModel: XYChartPopoverModel | null;
    onHoverSeries: (seriesIndex: number | null) => void;
    onHoverCategory: (categoryIndex: number | null) => void;
}
interface XYChartCanvasStageProps {
    layout: XYChartLayout | null;
    uniqueSvgId: string;
    svgRef: RefObject<SVGSVGElement | null>;
    hoveredSeriesIndex: number | null;
    hoveredCategoryIndex: number | null;
    hoverEntries: XYChartHoverEntry[];
    popoverModel: XYChartPopoverModel | null;
    onHoverSeries: (seriesIndex: number | null) => void;
    onHoverCategory: (categoryIndex: number | null) => void;
}
interface XYChartPopoverLayerProps {
    hoverEntries: XYChartHoverEntry[];
    popoverModel: XYChartPopoverModel | null;
}
function XYChartPopoverLayer({ hoverEntries, popoverModel, }: XYChartPopoverLayerProps): ReactElement {
    return (
      <div data-el="Cmarkdownxychart/PopoverLayer/Surface" style={POPOVER_LAYER_STYLE}>
        <AnimatePresence>
          {popoverModel != null && (<div style={{
                position: 'absolute',
                left: popoverModel.position.left + CANVAS_PADDING_PX,
                top: popoverModel.position.top + CANVAS_PADDING_PX,
            }}>
              <XYChartHoverPopover category={popoverModel.category} entries={hoverEntries} formatValue={formatXYChartHoverValue}/>
            </div>)}
        </AnimatePresence>
      </div>
    );
}
function XYChartCanvasStage({ layout, uniqueSvgId, svgRef, hoveredSeriesIndex, hoveredCategoryIndex, hoverEntries, popoverModel, onHoverSeries, onHoverCategory, }: XYChartCanvasStageProps): ReactElement | null {
    if (layout == null) {
        return null;
    }
    return (
      <div style={CANVAS_WRAP_STYLE}>
        <XYChartCanvas ref={svgRef} layout={layout} uniqueId={uniqueSvgId} hoveredSeriesIndex={hoveredSeriesIndex} hoveredCategoryIndex={hoveredCategoryIndex} onHoverSeries={onHoverSeries} onHoverCategory={onHoverCategory}/>
        <XYChartPopoverLayer hoverEntries={hoverEntries} popoverModel={popoverModel}/>
      </div>
    );
}
export function XYChartFrame({ parsed, layout, uniqueSvgId, viewportRef, svgRef, hoveredSeriesIndex, hoveredCategoryIndex, hoverEntries, popoverModel, onHoverSeries, onHoverCategory, }: XYChartFrameProps): ReactElement {
    return (
      <div data-el="Cmarkdownxychart/Frame/Root" style={FRAME_STYLE}>
        <div data-el="Cmarkdownxychart/Frame/Viewport" ref={viewportRef} style={SCROLL_VIEWPORT_STYLE} onMouseLeave={() => {
            onHoverCategory(null);
            onHoverSeries(null);
        }}>
          <div data-el="Cmarkdownxychart/Frame/Track" style={SCROLL_TRACK_STYLE}>
            <XYChartCanvasStage layout={layout} uniqueSvgId={uniqueSvgId} svgRef={svgRef} hoveredSeriesIndex={hoveredSeriesIndex} hoveredCategoryIndex={hoveredCategoryIndex} hoverEntries={hoverEntries} popoverModel={popoverModel} onHoverSeries={onHoverSeries} onHoverCategory={onHoverCategory}/>
          </div>
        </div>

        <XYChartLegend parsed={parsed} hoveredSeriesIndex={hoveredSeriesIndex} onHoverSeries={onHoverSeries} onHoverCategory={onHoverCategory}/>
      </div>
    );
}
